import React from 'react'
import { isTextAnimation } from '../utils/animationEngine'

/**
 * Animation Properties Panel — shows editable animation fields in the Properties sidebar
 * when an animation is applied to an item (shape, image, or text).
 *
 * Renders inside the element's properties panel (ShapeElementProperties, ImageElementProperties, etc.)
 */

// Animation type labels for display
const ANIMATION_TYPE_LABELS = {
  // Category 1: Entrance animations
  fadeIn: 'Fade In',
  riseUp: 'Rise Up',
  slideIn: 'Slide In',
  zoomIn: 'Zoom In',
  popIn: 'Pop In',
  bounceIn: 'Bounce In',
  typewriter: 'Typewriter',
  wordReveal: 'Word by Word',
  letterReveal: 'Letter by Letter',
  blurIn: 'Blur In',
  flipIn: 'Flip In',
  rotateIn: 'Rotate In',
  // Category 2: Emphasis animations
  emphasisPulse: 'Pulse',
  shake: 'Shake',
  emphasisBounce: 'Bounce',
  shimmer: 'Shimmer',
  glitchLoop: 'Glitch Loop',
  // Category 3: Exit animations
  fadeOut: 'Fade Out',
  slideOut: 'Slide Out',
  zoomOut: 'Zoom Out',
  blurOut: 'Blur Out',
  dissolve: 'Dissolve',
  // Category 4: Loop animations
  floating: 'Floating',
  swing: 'Swing',
  wave: 'Wave',
  breathing: 'Breathing',
  trackingLoop: 'Tracking Loop',
  // Category 5: Character & word presets
  staggeredRise: 'Staggered Rise',
  staggeredZoom: 'Staggered Zoom',
  charRotationCascade: 'Rotation Cascade',
  scatterAssemble: 'Scatter & Assemble',
  wordHighlightCascade: 'Word Highlight',
  // Category 6: Advanced reveals
  trackingReveal: 'Tracking Reveal',
  charFlipCascade: 'Flip Cascade',
  curtainReveal: 'Curtain Reveal',
  liquidReveal: 'Liquid Reveal',
  textDrawOn: 'Draw On',
  textBehindObject: 'Behind Object',
}

// Entrance direction options (includes top/bottom for slide-in)
const ENTRANCE_DIRECTION_OPTIONS = [
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
]

const EASING_OPTIONS = [
  { value: 'linear', label: 'Linear' },
  { value: 'easeIn', label: 'Ease In' },
  { value: 'easeOut', label: 'Ease Out' },
  { value: 'easeInOut', label: 'Ease In-Out' },
]

const DIRECTION_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
]

const AnimationPropertiesPanel = ({
  element,
  selectedElement,
  updateElementProperty,
}) => {
  const animations = element.animations
  if (!animations || animations.length === 0) return null

  const anim = animations[0]
  const animType = anim.type

  // Helper to update a single animation property
  const updateAnimProp = (key, value) => {
    const updated = { ...anim, [key]: value }
    updateElementProperty(selectedElement, 'animations', [updated])
  }

  // Helper to update a nested animation property (e.g., from.x, to.scale)
  const updateAnimNestedProp = (parentKey, childKey, value) => {
    const parent = anim[parentKey] || {}
    const updated = { ...anim, [parentKey]: { ...parent, [childKey]: value } }
    updateElementProperty(selectedElement, 'animations', [updated])
  }

  const removeAnimation = () => {
    updateElementProperty(selectedElement, 'animations', [])
  }

  return (
    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-blue-700">
          Animation: {ANIMATION_TYPE_LABELS[animType] || animType}
        </h4>
        <button
          onClick={removeAnimation}
          className="px-2 py-0.5 text-xs bg-red-100 text-red-600 hover:bg-red-200 rounded transition-colors"
        >
          Remove
        </button>
      </div>

      <div className="space-y-3">
        {/* Common: Timing */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Start (s)</label>
            <input
              type="number"
              value={anim.start ?? 0}
              onChange={(e) => updateAnimProp('start', Math.max(0, parseFloat(e.target.value) || 0))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              min="0"
              step="0.1"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Duration (s)</label>
            <input
              type="number"
              value={anim.duration ?? 2}
              onChange={(e) => updateAnimProp('duration', Math.max(0.1, parseFloat(e.target.value) || 2))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              min="0.1"
              step="0.1"
            />
          </div>
        </div>

        {/* Common: Easing */}
        <div>
          <label className="text-xs text-gray-500 block mb-1">Easing</label>
          <select
            value={anim.easing || 'linear'}
            onChange={(e) => updateAnimProp('easing', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {EASING_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Type-specific fields */}
        {renderTypeSpecificFields(anim, animType, updateAnimProp, updateAnimNestedProp)}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Type-specific field renderers
// ---------------------------------------------------------------------------

function renderTypeSpecificFields(anim, animType, updateAnimProp, updateAnimNestedProp) {
  switch (animType) {
    case 'fade':
      return <FadeFields anim={anim} update={updateAnimProp} />
    case 'motion':
      return <MotionFields anim={anim} updateNested={updateAnimNestedProp} />
    case 'zoom':
      return <ZoomFields anim={anim} updateNested={updateAnimNestedProp} />
    case 'rotate':
      return <RotateFields anim={anim} updateNested={updateAnimNestedProp} />
    case 'slide':
      return <SlideFields anim={anim} update={updateAnimProp} />
    case 'bounce':
      return <BounceFields anim={anim} update={updateAnimProp} />
    case 'pulse':
      return <PulseFields anim={anim} update={updateAnimProp} />
    case 'typing':
      return <TypingFields anim={anim} update={updateAnimProp} />
    case 'marquee':
      return <MarqueeFields anim={anim} update={updateAnimProp} />

    // ---- Category 1: Entrance animations ----
    case 'fadeIn':
      return <FadeInFields anim={anim} update={updateAnimProp} />
    case 'riseUp':
      return <RiseUpFields anim={anim} update={updateAnimProp} />
    case 'slideIn':
      return <SlideInFields anim={anim} update={updateAnimProp} />
    case 'zoomIn':
      return <ZoomInFields anim={anim} update={updateAnimProp} />
    case 'popIn':
      return <PopInFields anim={anim} update={updateAnimProp} />
    case 'bounceIn':
      return <BounceInFields anim={anim} update={updateAnimProp} />
    case 'blurIn':
      return <BlurInFields anim={anim} update={updateAnimProp} />
    case 'flipIn':
      return <FlipInFields anim={anim} update={updateAnimProp} />
    case 'rotateIn':
      return <RotateInFields anim={anim} update={updateAnimProp} />
    case 'typewriter':
      return <TypewriterFields anim={anim} update={updateAnimProp} />
    case 'wordReveal':
      return <WordRevealFields anim={anim} update={updateAnimProp} />
    case 'letterReveal':
      return <LetterRevealFields anim={anim} update={updateAnimProp} />

    // ---- Category 2: Emphasis animations ----
    case 'emphasisPulse':
      return <EmphasisPulseFields anim={anim} update={updateAnimProp} />
    case 'shake':
      return <ShakeFields anim={anim} update={updateAnimProp} />
    case 'emphasisBounce':
      return <EmphasisBounceFields anim={anim} update={updateAnimProp} />
    case 'shimmer':
      return <ShimmerFields anim={anim} update={updateAnimProp} />
    case 'glitchLoop':
      return <GlitchLoopFields anim={anim} update={updateAnimProp} />

    // ---- Category 3: Exit animations ----
    case 'fadeOut':
      return <FadeOutFields anim={anim} update={updateAnimProp} />
    case 'slideOut':
      return <SlideOutFields anim={anim} update={updateAnimProp} />
    case 'zoomOut':
      return <ZoomOutFields anim={anim} update={updateAnimProp} />
    case 'blurOut':
      return <BlurOutFields anim={anim} update={updateAnimProp} />
    case 'dissolve':
      return <DissolveFields anim={anim} update={updateAnimProp} />

    // ---- Category 4: Loop animations ----
    case 'floating':
      return <FloatingFields anim={anim} update={updateAnimProp} />
    case 'swing':
      return <SwingFields anim={anim} update={updateAnimProp} />
    case 'wave':
      return <WaveFields anim={anim} update={updateAnimProp} />
    case 'breathing':
      return <BreathingFields anim={anim} update={updateAnimProp} />
    case 'trackingLoop':
      return <TrackingLoopFields anim={anim} update={updateAnimProp} />

    // ---- Category 5: Character & word presets ----
    case 'staggeredRise':
      return <StaggeredRiseFields anim={anim} update={updateAnimProp} />
    case 'staggeredZoom':
      return <StaggeredZoomFields anim={anim} update={updateAnimProp} />
    case 'charRotationCascade':
      return <CharRotationCascadeFields anim={anim} update={updateAnimProp} />
    case 'scatterAssemble':
      return <ScatterAssembleFields anim={anim} update={updateAnimProp} />
    case 'wordHighlightCascade':
      return <WordHighlightCascadeFields anim={anim} update={updateAnimProp} />

    // ---- Category 6: Advanced reveals ----
    case 'trackingReveal':
      return <TrackingRevealFields anim={anim} update={updateAnimProp} />
    case 'charFlipCascade':
      return <CharFlipCascadeFields anim={anim} update={updateAnimProp} />
    case 'curtainReveal':
      return <CurtainRevealFields anim={anim} update={updateAnimProp} />
    case 'liquidReveal':
      return <LiquidRevealFields anim={anim} update={updateAnimProp} />
    case 'textDrawOn':
      return <WipeRevealFields anim={anim} update={updateAnimProp} />
    case 'textBehindObject':
      return <WipeRevealFields anim={anim} update={updateAnimProp} />
    case 'ascend':
    case 'shift':
    case 'merge':
    case 'burst':
    case 'bounceText':
    case 'roll':
    case 'skate':
    case 'spread':
    case 'clarify':
      return <StaggeredFields anim={anim} animType={animType} update={updateAnimProp} />
    default:
      return null
  }
}

// --- Fade ---
function FadeFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Fade In</label>
        <input
          type="number"
          value={anim.in ?? 0.5}
          onChange={(e) => update('in', Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="1" step="0.05"
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Fade Out</label>
        <input
          type="number"
          value={anim.out ?? 0.5}
          onChange={(e) => update('out', Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="1" step="0.05"
        />
      </div>
    </div>
  )
}

// --- Motion ---
function MotionFields({ anim, updateNested }) {
  const from = anim.from || {}
  const to = anim.to || {}
  return (
    <>
      <p className="text-xs text-gray-500 font-medium">From</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-400 block mb-1">X</label>
          <input type="number" value={from.x ?? 0}
            onChange={(e) => updateNested('from', 'x', parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" step="1" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Y</label>
          <input type="number" value={from.y ?? 0}
            onChange={(e) => updateNested('from', 'y', parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" step="1" />
        </div>
      </div>
      <p className="text-xs text-gray-500 font-medium mt-2">To</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-400 block mb-1">X</label>
          <input type="number" value={to.x ?? 0}
            onChange={(e) => updateNested('to', 'x', parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" step="1" />
        </div>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Y</label>
          <input type="number" value={to.y ?? 0}
            onChange={(e) => updateNested('to', 'y', parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" step="1" />
        </div>
      </div>
    </>
  )
}

// --- Zoom ---
function ZoomFields({ anim, updateNested }) {
  const from = anim.from || {}
  const to = anim.to || {}
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">From Scale</label>
        <input type="number" value={from.scale ?? 0.5}
          onChange={(e) => updateNested('from', 'scale', Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="5" step="0.1" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">To Scale</label>
        <input type="number" value={to.scale ?? 1.0}
          onChange={(e) => updateNested('to', 'scale', Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="5" step="0.1" />
      </div>
    </div>
  )
}

// --- Rotate ---
function RotateFields({ anim, updateNested }) {
  const from = anim.from || {}
  const to = anim.to || {}
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">From (°)</label>
        <input type="number" value={from.rotation ?? 0}
          onChange={(e) => updateNested('from', 'rotation', parseFloat(e.target.value) || 0)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          step="15" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">To (°)</label>
        <input type="number" value={to.rotation ?? 360}
          onChange={(e) => updateNested('to', 'rotation', parseFloat(e.target.value) || 0)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          step="15" />
      </div>
    </div>
  )
}

// --- Slide ---
function SlideFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Direction</label>
        <select value={anim.direction || 'left'}
          onChange={(e) => update('direction', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          {DIRECTION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Distance (px)</label>
        <input type="number" value={anim.distance ?? 200}
          onChange={(e) => update('distance', Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" step="10" />
      </div>
    </div>
  )
}

// --- Bounce ---
function BounceFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Intensity</label>
        <input type="number" value={anim.intensity ?? 1.2}
          onChange={(e) => update('intensity', Math.max(1, parseFloat(e.target.value) || 1.2))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="1" max="3" step="0.1" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Bounces</label>
        <input type="number" value={anim.bounces ?? 3}
          onChange={(e) => update('bounces', Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="1" max="10" step="1" />
      </div>
    </div>
  )
}

// --- Pulse ---
function PulseFields({ anim, update }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Min Scale</label>
        <input type="number" value={anim.minScale ?? 0.9}
          onChange={(e) => update('minScale', Math.max(0.1, parseFloat(e.target.value) || 0.9))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0.1" max="2" step="0.05" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Max Scale</label>
        <input type="number" value={anim.maxScale ?? 1.1}
          onChange={(e) => update('maxScale', Math.max(0.1, parseFloat(e.target.value) || 1.1))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0.1" max="3" step="0.05" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Pulses</label>
        <input type="number" value={anim.pulses ?? 2}
          onChange={(e) => update('pulses', Math.max(1, Math.min(20, parseInt(e.target.value) || 2)))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="1" max="20" step="1" />
      </div>
    </div>
  )
}

// --- Typing ---
function TypingFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Speed (ms/char)</label>
        <input type="number" value={anim.speed ?? 50}
          onChange={(e) => update('speed', Math.max(10, parseInt(e.target.value) || 50))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="10" max="500" step="10" />
      </div>
      <div className="flex items-end pb-1">
        <label className="flex items-center space-x-2 text-sm text-gray-600">
          <input type="checkbox" checked={anim.showCursor !== false}
            onChange={(e) => update('showCursor', e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
          <span className="text-xs">Show Cursor</span>
        </label>
      </div>
    </div>
  )
}

// --- Marquee ---
function MarqueeFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Speed (px/s)</label>
          <input type="number" value={anim.speed ?? 100}
            onChange={(e) => update('speed', Math.max(10, parseInt(e.target.value) || 100))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="10" max="1000" step="10" />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Direction</label>
          <select value={anim.direction || 'left'}
            onChange={(e) => update('direction', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Gap (px)</label>
        <input type="number" value={anim.gap ?? 60}
          onChange={(e) => update('gap', Math.max(0, parseInt(e.target.value) || 60))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="500" step="10" />
      </div>
    </div>
  )
}

// --- Staggered text animations (ascend, shift, merge, burst, bounceText, roll, skate, spread, clarify) ---
function StaggeredFields({ anim, animType, update }) {
  return (
    <div className="space-y-2">
      {/* Stagger */}
      <div>
        <label className="text-xs text-gray-500 block mb-1">Stagger (s)</label>
        <input type="number" value={anim.stagger ?? 0.1}
          onChange={(e) => update('stagger', Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          min="0" max="2" step="0.01" />
      </div>

      {/* Distance (ascend, shift, merge, skate, spread) */}
      {['ascend', 'shift', 'merge', 'skate', 'spread'].includes(animType) && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Distance (px)</label>
          <input type="number" value={anim.distance ?? 50}
            onChange={(e) => update('distance', Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="0" step="5" />
        </div>
      )}

      {/* Direction (shift, merge, roll) */}
      {animType === 'shift' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Direction</label>
          <select value={anim.direction || 'horizontal'}
            onChange={(e) => update('direction', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </select>
        </div>
      )}

      {animType === 'roll' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Direction</label>
          <select value={anim.direction || 'clockwise'}
            onChange={(e) => update('direction', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="clockwise">Clockwise</option>
            <option value="counterclockwise">Counter-clockwise</option>
          </select>
        </div>
      )}

      {/* Intensity (burst) */}
      {animType === 'burst' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Intensity</label>
          <input type="number" value={anim.intensity ?? 1.5}
            onChange={(e) => update('intensity', Math.max(1, parseFloat(e.target.value) || 1.5))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="1" max="5" step="0.1" />
        </div>
      )}

      {/* Height (bounceText) */}
      {animType === 'bounceText' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Height (px)</label>
          <input type="number" value={anim.height ?? 20}
            onChange={(e) => update('height', Math.max(1, parseFloat(e.target.value) || 20))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="1" max="200" step="5" />
        </div>
      )}

      {/* Wobble (skate) */}
      {animType === 'skate' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Wobble (px)</label>
          <input type="number" value={anim.wobble ?? 10}
            onChange={(e) => update('wobble', Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="0" max="100" step="1" />
        </div>
      )}

      {/* Blur Amount (clarify) */}
      {animType === 'clarify' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Blur Amount (px)</label>
          <input type="number" value={anim.blurAmount ?? 10}
            onChange={(e) => update('blurAmount', Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="0" max="50" step="1" />
        </div>
      )}

      {/* Block Size (block) */}
      {animType === 'block' && (
        <div>
          <label className="text-xs text-gray-500 block mb-1">Block Size</label>
          <select value={anim.blockSize || 'word'}
            onChange={(e) => update('blockSize', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="word">Word</option>
            <option value="line">Line</option>
          </select>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 1: Entrance animation field renderers
// ---------------------------------------------------------------------------

// Small shared input helpers to keep the entrance panels compact.
function NumField({ label, value, fallback, min, max, step, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <input type="number" value={value ?? fallback}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        min={min} max={max} step={step} />
    </div>
  )
}

function SelectField({ label, value, fallback, options, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <select value={value || fallback}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  )
}

function CheckField({ label, value, fallback, onChange }) {
  const checked = value !== undefined ? value : fallback
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600">
      <input type="checkbox" checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-400" />
      {label}
    </label>
  )
}

function FadeInFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <NumField label="Start Opacity" value={anim.startOpacity} fallback={0} min={0} max={1} step={0.05}
        onChange={(v) => update('startOpacity', Math.max(0, Math.min(1, v || 0)))} />
      <NumField label="End Opacity" value={anim.endOpacity} fallback={1} min={0} max={1} step={0.05}
        onChange={(v) => update('endOpacity', Math.max(0, Math.min(1, isNaN(v) ? 1 : v)))} />
    </div>
  )
}

function RiseUpFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="up" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <NumField label="Distance (px)" value={anim.distance} fallback={80} min={0} step={5}
        onChange={(v) => update('distance', Math.max(0, v || 0))} />
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function SlideInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="left"
        options={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }, { value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }]}
        onChange={(v) => update('direction', v)} />
      <NumField label="Distance (px)" value={anim.distance} fallback={200} min={0} step={10}
        onChange={(v) => update('distance', Math.max(0, v || 0))} />
      <CheckField label="Fade in" value={anim.fade} fallback={false} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function ZoomInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Scale" value={anim.startScale} fallback={0.5} min={0} max={3} step={0.05}
          onChange={(v) => update('startScale', Math.max(0, v || 0))} />
        <NumField label="End Scale" value={anim.endScale} fallback={1} min={0} max={3} step={0.05}
          onChange={(v) => update('endScale', Math.max(0, isNaN(v) ? 1 : v))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function PopInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <NumField label="Start" value={anim.startScale} fallback={0.2} min={0} max={2} step={0.05}
          onChange={(v) => update('startScale', Math.max(0, v || 0))} />
        <NumField label="Overshoot" value={anim.overshoot} fallback={1.15} min={1} max={2} step={0.05}
          onChange={(v) => update('overshoot', Math.max(1, isNaN(v) ? 1.15 : v))} />
        <NumField label="End" value={anim.endScale} fallback={1} min={0} max={2} step={0.05}
          onChange={(v) => update('endScale', Math.max(0, isNaN(v) ? 1 : v))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function BounceInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Mode" value={anim.mode} fallback="scale"
        options={[{ value: 'scale', label: 'Scale' }, { value: 'position', label: 'Position' }]}
        onChange={(v) => update('mode', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Intensity" value={anim.intensity} fallback={1.2} min={1} max={3} step={0.05}
          onChange={(v) => update('intensity', Math.max(1, isNaN(v) ? 1.2 : v))} />
        <NumField label="Bounces" value={anim.bounces} fallback={3} min={1} max={8} step={1}
          onChange={(v) => update('bounces', Math.max(1, Math.round(v || 3)))} />
      </div>
      {anim.mode === 'position' && (
        <>
          <SelectField label="Direction" value={anim.direction} fallback="up" options={ENTRANCE_DIRECTION_OPTIONS}
            onChange={(v) => update('direction', v)} />
          <NumField label="Distance (px)" value={anim.distance} fallback={80} min={0} step={5}
            onChange={(v) => update('distance', Math.max(0, v || 0))} />
        </>
      )}
    </div>
  )
}

function BlurInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Blur (px)" value={anim.startBlur} fallback={12} min={0} max={50} step={1}
          onChange={(v) => update('startBlur', Math.max(0, v || 0))} />
        <NumField label="End Blur (px)" value={anim.endBlur} fallback={0} min={0} max={50} step={1}
          onChange={(v) => update('endBlur', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function FlipInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Axis" value={anim.axis} fallback="x"
        options={[{ value: 'x', label: 'Horizontal (X)' }, { value: 'y', label: 'Vertical (Y)' }]}
        onChange={(v) => update('axis', v)} />
      <NumField label="Start Angle (deg)" value={anim.startAngle} fallback={90} min={0} max={180} step={5}
        onChange={(v) => update('startAngle', v || 0)} />
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function RotateInFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Angle" value={anim.startAngle} fallback={-180} min={-720} max={720} step={15}
          onChange={(v) => update('startAngle', isNaN(v) ? -180 : v)} />
        <NumField label="End Angle" value={anim.endAngle} fallback={0} min={-720} max={720} step={15}
          onChange={(v) => update('endAngle', isNaN(v) ? 0 : v)} />
      </div>
      <NumField label="Extra Turns" value={anim.turns} fallback={0} min={0} max={5} step={1}
        onChange={(v) => update('turns', Math.max(0, Math.round(v || 0)))} />
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function TypewriterFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <NumField label="Speed (chars/sec)" value={anim.cps} fallback={20} min={1} max={100} step={1}
        onChange={(v) => update('cps', Math.max(1, Math.round(v || 20)))} />
      <CheckField label="Show cursor" value={anim.showCursor} fallback={true} onChange={(v) => update('showCursor', v)} />
    </div>
  )
}

function WordRevealFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <NumField label="Word Delay (s)" value={anim.wordDelay} fallback={0.2} min={0} max={2} step={0.05}
        onChange={(v) => update('wordDelay', Math.max(0, v || 0))} />
    </div>
  )
}

function LetterRevealFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <NumField label="Stagger (s)" value={anim.stagger} fallback={0.05} min={0} max={1} step={0.01}
        onChange={(v) => update('stagger', Math.max(0, v || 0))} />
      <SelectField label="Direction" value={anim.direction} fallback="up" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Distance (px)" value={anim.distance} fallback={20} min={0} max={200} step={2}
          onChange={(v) => update('distance', Math.max(0, v || 0))} />
        <NumField label="Rotate (deg)" value={anim.rotate} fallback={0} min={-180} max={180} step={5}
          onChange={(v) => update('rotate', v || 0)} />
      </div>
      <NumField label="Start Scale" value={anim.startScale} fallback={1} min={0} max={3} step={0.05}
        onChange={(v) => update('startScale', Math.max(0, isNaN(v) ? 1 : v))} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 2: Emphasis animation field renderers
// ---------------------------------------------------------------------------

function ColorField({ label, value, fallback, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <input type="color" value={value || fallback}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 border border-gray-300 rounded px-1 py-0.5 cursor-pointer" />
    </div>
  )
}

function EmphasisPulseFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Min Scale" value={anim.minScale} fallback={1} min={0.1} max={2} step={0.05}
          onChange={(v) => update('minScale', Math.max(0.1, isNaN(v) ? 1 : v))} />
        <NumField label="Max Scale" value={anim.maxScale} fallback={1.15} min={0.1} max={3} step={0.05}
          onChange={(v) => update('maxScale', Math.max(0.1, isNaN(v) ? 1.15 : v))} />
      </div>
      <NumField label="Pulse Count" value={anim.pulseCount} fallback={2} min={1} max={10} step={1}
        onChange={(v) => update('pulseCount', Math.max(1, Math.round(v || 2)))} />
    </div>
  )
}

function ShakeFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Strength (px)" value={anim.strength} fallback={8} min={0} max={100} step={1}
          onChange={(v) => update('strength', Math.max(0, v || 0))} />
        <NumField label="Frequency" value={anim.frequency} fallback={12} min={1} max={60} step={1}
          onChange={(v) => update('frequency', Math.max(1, Math.round(v || 12)))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Rotation (deg)" value={anim.rotateAmount} fallback={0} min={0} max={45} step={1}
          onChange={(v) => update('rotateAmount', Math.max(0, v || 0))} />
        <NumField label="Damping" value={anim.damping} fallback={1} min={0} max={5} step={0.25}
          onChange={(v) => update('damping', Math.max(0, v || 0))} />
      </div>
      <SelectField label="Direction" value={anim.direction} fallback="both"
        options={[{ value: 'both', label: 'Both' }, { value: 'horizontal', label: 'Horizontal' }, { value: 'vertical', label: 'Vertical' }]}
        onChange={(v) => update('direction', v)} />
    </div>
  )
}

function EmphasisBounceFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Mode" value={anim.mode} fallback="position"
        options={[{ value: 'position', label: 'Position' }, { value: 'scale', label: 'Scale' }]}
        onChange={(v) => update('mode', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Distance (px)" value={anim.distance} fallback={24} min={0} max={200} step={2}
          onChange={(v) => update('distance', Math.max(0, v || 0))} />
        <NumField label="Bounces" value={anim.bounces} fallback={3} min={1} max={10} step={1}
          onChange={(v) => update('bounces', Math.max(1, Math.round(v || 3)))} />
      </div>
      <NumField label="Damping" value={anim.damping} fallback={2} min={0} max={5} step={0.25}
        onChange={(v) => update('damping', Math.max(0, v || 0))} />
    </div>
  )
}

function ShimmerFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <ColorField label="Highlight Color" value={anim.color} fallback="#ffffff"
          onChange={(v) => update('color', v)} />
        <NumField label="Brightness" value={anim.brightness} fallback={0.8} min={0} max={1} step={0.05}
          onChange={(v) => update('brightness', Math.max(0, Math.min(1, isNaN(v) ? 0.8 : v)))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Width (0-1)" value={anim.width} fallback={0.25} min={0.05} max={1} step={0.05}
          onChange={(v) => update('width', Math.max(0.05, Math.min(1, isNaN(v) ? 0.25 : v)))} />
        <NumField label="Sweeps" value={anim.speed} fallback={1} min={0.1} max={10} step={0.5}
          onChange={(v) => update('speed', Math.max(0.1, v || 1))} />
      </div>
      <NumField label="Angle (deg)" value={anim.angle} fallback={20} min={-90} max={90} step={5}
        onChange={(v) => update('angle', isNaN(v) ? 20 : v)} />
      <CheckField label="Repeat" value={anim.repeat} fallback={true} onChange={(v) => update('repeat', v)} />
    </div>
  )
}

function GlitchLoopFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Bursts" value={anim.bursts} fallback={4} min={1} max={20} step={1}
          onChange={(v) => update('bursts', Math.max(1, Math.round(v || 4)))} />
        <NumField label="Burst Len (0-1)" value={anim.burstDuration} fallback={0.18} min={0.02} max={1} step={0.02}
          onChange={(v) => update('burstDuration', Math.max(0.02, Math.min(1, isNaN(v) ? 0.18 : v)))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Displacement (px)" value={anim.intensity} fallback={8} min={0} max={50} step={1}
          onChange={(v) => update('intensity', Math.max(0, v || 0))} />
        <NumField label="RGB Offset (px)" value={anim.rgbOffset} fallback={4} min={0} max={30} step={1}
          onChange={(v) => update('rgbOffset', Math.max(0, v || 0))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Flicker (0-1)" value={anim.flicker} fallback={0.3} min={0} max={1} step={0.05}
          onChange={(v) => update('flicker', Math.max(0, Math.min(1, isNaN(v) ? 0.3 : v)))} />
        <NumField label="Seed" value={anim.seed} fallback={1} min={0} max={9999} step={1}
          onChange={(v) => update('seed', Math.max(0, Math.round(v || 1)))} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 3: Exit animation field renderers
// ---------------------------------------------------------------------------

function FadeOutFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <NumField label="Start Opacity" value={anim.startOpacity} fallback={1} min={0} max={1} step={0.05}
        onChange={(v) => update('startOpacity', Math.max(0, Math.min(1, isNaN(v) ? 1 : v)))} />
      <NumField label="End Opacity" value={anim.endOpacity} fallback={0} min={0} max={1} step={0.05}
        onChange={(v) => update('endOpacity', Math.max(0, Math.min(1, v || 0)))} />
    </div>
  )
}

function SlideOutFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="left" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <NumField label="Distance (px)" value={anim.distance} fallback={200} min={0} step={10}
        onChange={(v) => update('distance', Math.max(0, v || 0))} />
      <CheckField label="Fade out" value={anim.fade} fallback={false} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function ZoomOutFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Scale" value={anim.startScale} fallback={1} min={0} max={3} step={0.05}
          onChange={(v) => update('startScale', Math.max(0, isNaN(v) ? 1 : v))} />
        <NumField label="End Scale" value={anim.endScale} fallback={0} min={0} max={3} step={0.05}
          onChange={(v) => update('endScale', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade out" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function BlurOutFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Blur (px)" value={anim.startBlur} fallback={0} min={0} max={50} step={1}
          onChange={(v) => update('startBlur', Math.max(0, v || 0))} />
        <NumField label="End Blur (px)" value={anim.endBlur} fallback={12} min={0} max={50} step={1}
          onChange={(v) => update('endBlur', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade out" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function DissolveFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <NumField label="Opacity Falloff" value={anim.falloff} fallback={1} min={0.2} max={4} step={0.1}
        onChange={(v) => update('falloff', Math.max(0.2, isNaN(v) ? 1 : v))} />
      <NumField label="Scatter (px)" value={anim.scatter} fallback={0} min={0} max={200} step={2}
        onChange={(v) => update('scatter', Math.max(0, v || 0))} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 4: Loop animation field renderers
// ---------------------------------------------------------------------------

function FloatingFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Axis" value={anim.axis} fallback="vertical"
        options={[{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]}
        onChange={(v) => update('axis', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Amplitude (px)" value={anim.amplitude} fallback={10} min={0} max={200} step={1}
          onChange={(v) => update('amplitude', Math.max(0, v || 0))} />
        <NumField label="Cycles" value={anim.cycles} fallback={2} min={1} max={20} step={1}
          onChange={(v) => update('cycles', Math.max(1, Math.round(v || 2)))} />
      </div>
      <NumField label="Sway (deg)" value={anim.rotate} fallback={0} min={0} max={45} step={1}
        onChange={(v) => update('rotate', Math.max(0, v || 0))} />
    </div>
  )
}

function SwingFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Angle (deg)" value={anim.angle} fallback={8} min={0} max={90} step={1}
          onChange={(v) => update('angle', Math.max(0, v || 0))} />
        <NumField label="Cycles" value={anim.cycles} fallback={3} min={1} max={20} step={1}
          onChange={(v) => update('cycles', Math.max(1, Math.round(v || 3)))} />
      </div>
      <NumField label="Damping (0 = loop)" value={anim.damping} fallback={0} min={0} max={5} step={0.25}
        onChange={(v) => update('damping', Math.max(0, v || 0))} />
    </div>
  )
}

function WaveFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Amplitude (px)" value={anim.amplitude} fallback={10} min={0} max={200} step={1}
          onChange={(v) => update('amplitude', Math.max(0, v || 0))} />
        <NumField label="Cycles" value={anim.cycles} fallback={2} min={1} max={20} step={1}
          onChange={(v) => update('cycles', Math.max(1, Math.round(v || 2)))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Char Phase" value={anim.charPhase} fallback={0.15} min={0} max={1} step={0.01}
          onChange={(v) => update('charPhase', Math.max(0, v || 0))} />
        <NumField label="Rotate (deg)" value={anim.rotate} fallback={0} min={0} max={45} step={1}
          onChange={(v) => update('rotate', Math.max(0, v || 0))} />
      </div>
    </div>
  )
}

function BreathingFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Min Scale" value={anim.minScale} fallback={1} min={0.5} max={2} step={0.01}
          onChange={(v) => update('minScale', Math.max(0.5, isNaN(v) ? 1 : v))} />
        <NumField label="Max Scale" value={anim.maxScale} fallback={1.03} min={0.5} max={2} step={0.01}
          onChange={(v) => update('maxScale', Math.max(0.5, isNaN(v) ? 1.03 : v))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Cycles" value={anim.cycles} fallback={1} min={1} max={20} step={1}
          onChange={(v) => update('cycles', Math.max(1, Math.round(v || 1)))} />
        <NumField label="Opacity Var (0-1)" value={anim.opacityVar} fallback={0} min={0} max={1} step={0.05}
          onChange={(v) => update('opacityVar', Math.max(0, Math.min(1, v || 0)))} />
      </div>
    </div>
  )
}

function TrackingLoopFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Min Tracking (px)" value={anim.minTracking} fallback={0} min={0} max={100} step={1}
          onChange={(v) => update('minTracking', Math.max(0, v || 0))} />
        <NumField label="Max Tracking (px)" value={anim.maxTracking} fallback={12} min={0} max={100} step={1}
          onChange={(v) => update('maxTracking', Math.max(0, v || 0))} />
      </div>
      <NumField label="Cycles" value={anim.cycles} fallback={2} min={1} max={20} step={1}
        onChange={(v) => update('cycles', Math.max(1, Math.round(v || 2)))} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 5: Character & Word preset field renderers
// ---------------------------------------------------------------------------

function StaggeredRiseFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="up" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Distance (px)" value={anim.distance} fallback={40} min={0} max={300} step={5}
          onChange={(v) => update('distance', Math.max(0, v || 0))} />
        <NumField label="Stagger (s)" value={anim.stagger} fallback={0.05} min={0} max={1} step={0.01}
          onChange={(v) => update('stagger', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function StaggeredZoomFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Scale" value={anim.startScale} fallback={0.2} min={0} max={2} step={0.05}
          onChange={(v) => update('startScale', Math.max(0, v || 0))} />
        <NumField label="Stagger (s)" value={anim.stagger} fallback={0.05} min={0} max={1} step={0.01}
          onChange={(v) => update('stagger', Math.max(0, v || 0))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Overshoot" value={anim.overshoot} fallback={0} min={0} max={1} step={0.05}
          onChange={(v) => update('overshoot', Math.max(0, v || 0))} />
        <NumField label="Rotate (deg)" value={anim.rotate} fallback={0} min={-180} max={180} step={5}
          onChange={(v) => update('rotate', v || 0)} />
      </div>
    </div>
  )
}

function CharRotationCascadeFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Angle (deg)" value={anim.angle} fallback={-90} min={-360} max={360} step={15}
          onChange={(v) => update('angle', isNaN(v) ? -90 : v)} />
        <NumField label="Stagger (s)" value={anim.stagger} fallback={0.05} min={0} max={1} step={0.01}
          onChange={(v) => update('stagger', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function ScatterAssembleFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Scatter (px)" value={anim.distance} fallback={120} min={0} max={600} step={10}
          onChange={(v) => update('distance', Math.max(0, v || 0))} />
        <NumField label="Rotate (deg)" value={anim.rotate} fallback={90} min={0} max={360} step={15}
          onChange={(v) => update('rotate', Math.max(0, v || 0))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Stagger (s)" value={anim.stagger} fallback={0.03} min={0} max={1} step={0.01}
          onChange={(v) => update('stagger', Math.max(0, v || 0))} />
        <NumField label="Seed" value={anim.seed} fallback={1} min={0} max={9999} step={1}
          onChange={(v) => update('seed', Math.max(0, Math.round(v || 1)))} />
      </div>
    </div>
  )
}

function WordHighlightCascadeFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <ColorField label="Highlight Color" value={anim.highlightColor} fallback="#ffd400"
          onChange={(v) => update('highlightColor', v)} />
        <NumField label="Scale" value={anim.scale} fallback={1.15} min={1} max={2} step={0.05}
          onChange={(v) => update('scale', Math.max(1, isNaN(v) ? 1.15 : v))} />
      </div>
      <CheckField label="Keep previous words highlighted" value={anim.persist} fallback={false}
        onChange={(v) => update('persist', v)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Category 6: Advanced reveal field renderers
// ---------------------------------------------------------------------------

function TrackingRevealFields({ anim, update }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <NumField label="Tracking (px)" value={anim.tracking} fallback={24} min={0} max={200} step={2}
        onChange={(v) => update('tracking', Math.max(0, v || 0))} />
      <NumField label="Stagger (s)" value={anim.stagger} fallback={0.03} min={0} max={1} step={0.01}
        onChange={(v) => update('stagger', Math.max(0, v || 0))} />
    </div>
  )
}

function CharFlipCascadeFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Axis" value={anim.axis} fallback="y"
        options={[{ value: 'y', label: 'Vertical (Y)' }, { value: 'x', label: 'Horizontal (X)' }]}
        onChange={(v) => update('axis', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Start Angle (deg)" value={anim.startAngle} fallback={90} min={0} max={180} step={5}
          onChange={(v) => update('startAngle', Math.max(0, v || 0))} />
        <NumField label="Stagger (s)" value={anim.stagger} fallback={0.05} min={0} max={1} step={0.01}
          onChange={(v) => update('stagger', Math.max(0, v || 0))} />
      </div>
      <CheckField label="Fade in" value={anim.fade} fallback={true} onChange={(v) => update('fade', v)} />
    </div>
  )
}

function CurtainRevealFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="horizontal"
        options={[{ value: 'horizontal', label: 'Horizontal (open L/R)' }, { value: 'vertical', label: 'Vertical (open U/D)' }]}
        onChange={(v) => update('direction', v)} />
      <NumField label="Softness (0-0.3)" value={anim.softness} fallback={0.05} min={0} max={0.3} step={0.01}
        onChange={(v) => update('softness', Math.max(0, Math.min(0.3, v || 0)))} />
      <CheckField label="Reverse (close)" value={anim.reverse} fallback={false} onChange={(v) => update('reverse', v)} />
    </div>
  )
}

function LiquidRevealFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="left" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Wave (0-0.2)" value={anim.wave} fallback={0.08} min={0} max={0.2} step={0.01}
          onChange={(v) => update('wave', Math.max(0, Math.min(0.2, v || 0)))} />
        <NumField label="Ripples" value={anim.frequency} fallback={3} min={1} max={12} step={1}
          onChange={(v) => update('frequency', Math.max(1, Math.round(v || 3)))} />
      </div>
      <NumField label="Softness (0-0.3)" value={anim.softness} fallback={0.08} min={0} max={0.3} step={0.01}
        onChange={(v) => update('softness', Math.max(0, Math.min(0.3, v || 0)))} />
    </div>
  )
}

// Shared fields for the directional-wipe reveals (textDrawOn, textBehindObject).
function WipeRevealFields({ anim, update }) {
  return (
    <div className="space-y-2">
      <SelectField label="Direction" value={anim.direction} fallback="left" options={ENTRANCE_DIRECTION_OPTIONS}
        onChange={(v) => update('direction', v)} />
      <NumField label="Softness (0-0.3)" value={anim.softness} fallback={0.02} min={0} max={0.3} step={0.01}
        onChange={(v) => update('softness', Math.max(0, Math.min(0.3, v || 0)))} />
    </div>
  )
}

export default AnimationPropertiesPanel
